import { useCallback, useEffect, useState } from "react";
import {
  planAutoEntry,
  readRememberedEntry,
  resolveAfterProbe,
  serverEntryLinks,
  serverOnlineState,
  writeRememberedEntry,
} from "../lib/serverLinks";
import { probePublicEntry } from "../lib/publicProbe";

export interface ServerEntryCardProps {
  id: string;
  label: string;
  serverId: string;
  relationship: string;
  lastLanIp: string | null;
  lastLanPort: number | null;
  lastSeenAt: string | null;
  // True when this is the account's only server: auto-connect fires on
  // mount instead of waiting for a manual "Open" tap (Jellyfin-style
  // multi-server accounts always require the tap — see AccountHomePage).
  autoConnect: boolean;
  // Hides the title/id row when the caller (ServersPage) already renders
  // its own header for this install.
  showHeader?: boolean;
}

export function ServerEntryCard(props: ServerEntryCardProps) {
  const { showHeader = true } = props;
  const links = serverEntryLinks({
    last_lan_ip: props.lastLanIp,
    last_lan_port: props.lastLanPort,
    last_seen_at: props.lastSeenAt,
  });
  const online = serverOnlineState({ last_seen_at: props.lastSeenAt });
  const [connecting, setConnecting] = useState(false);

  const connect = useCallback(async () => {
    const remembered = readRememberedEntry(props.id);
    const plan = planAutoEntry(links, online, remembered);
    if (plan.action === "offline") return;
    if (plan.action === "navigate" && plan.url && plan.choice) {
      writeRememberedEntry(props.id, plan.choice);
      window.location.href = plan.url;
      return;
    }
    // plan.action === "probe": same-scheme public check, then decide.
    setConnecting(true);
    const reachable = links.awayUrl ? await probePublicEntry(links.awayUrl) : false;
    const resolved = resolveAfterProbe(links, reachable);
    if (resolved.action === "navigate" && resolved.url && resolved.choice) {
      writeRememberedEntry(props.id, resolved.choice);
      window.location.href = resolved.url;
    } else {
      setConnecting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links.homeUrl, links.awayUrl, online, props.id]);

  useEffect(() => {
    if (props.autoConnect) connect();
    // Auto-connect only on first mount for this server, not on every
    // link/online recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.autoConnect]);

  return (
    <div className="vstack" style={{ gap: 10 }}>
      {showHeader && (
        <div className="hstack" style={{ justifyContent: "space-between" }}>
          <div>
            <div className="row-title">{props.label}</div>
            <div className="row-meta">{props.serverId} · {props.relationship}</div>
          </div>
          {!props.autoConnect && online === "online" && (
            <button className="btn primary" onClick={connect} disabled={connecting}>
              {connecting ? "Connecting…" : "Open"}
            </button>
          )}
        </div>
      )}

      {connecting && <p className="meta">Connecting to {props.label}…</p>}

      {online === "never_seen" && (
        <p className="meta warn">
          Offline — never seen. Open Prism once on the home server to activate entry links.
        </p>
      )}
      {online === "stale" && (
        <p className="meta warn">May be offline — last seen {props.lastSeenAt}.</p>
      )}

      {links.homeUrl && links.awayUrl && (
        <div className="hstack" style={{ gap: 32 }}>
          <div>
            <a className="btn" href={links.homeUrl} rel="noopener noreferrer"
              onClick={() => writeRememberedEntry(props.id, "home")}>
              Open at home
            </a>
            <div className="subtle">use when you're on your home WiFi</div>
          </div>
          <div>
            <a className="btn" href={links.awayUrl} rel="noopener noreferrer"
              onClick={() => writeRememberedEntry(props.id, "away")}>
              Open away
            </a>
            <div className="subtle">use everywhere else</div>
          </div>
        </div>
      )}
    </div>
  );
}
